import React, { useRef, useCallback, useState } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import { Box, CircularProgress, Dialog, IconButton, Typography } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";

interface MonacoJsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: string | number;
  placeholder?: string;
  readOnly?: boolean;
}

/**
 * Monaco Editor configured for JSON editing with auto-format on type
 */
export const MonacoJsonEditor: React.FC<MonacoJsonEditorProps> = ({
  value,
  onChange,
  height = 400,
  readOnly = false,
}) => {
  const editorRef = useRef<any>(null);
  const [expanded, setExpanded] = useState(false);

  const handleChange = useCallback(
    (newValue: string | undefined) => {
      onChange(newValue || "");
    },
    [onChange]
  );

  // Set up editor with formatting
  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Set up format on type
    editor.getModel()?.updateOptions({
      tabSize: 2,
      insertSpaces: true,
    });

    // Add keyboard shortcut for format (Shift+Alt+F or Cmd+Shift+F)
    editor.addAction({
      id: "format-json",
      label: "Format JSON",
      keybindings: [
        monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
      ],
      run: () => {
        editor.getAction("editor.action.formatDocument")?.run();
      },
    });
  };

  const editorOptions = {
    minimap: { enabled: false },
    lineNumbers: "on" as const,
    fontSize: 13,
    fontFamily: "Monaco, Menlo, 'Ubuntu Mono', Consolas, monospace",
    tabSize: 2,
    insertSpaces: true,
    formatOnType: true,
    formatOnPaste: true,
    autoIndent: "full" as const,
    scrollBeyondLastLine: false,
    wordWrap: "on" as const,
    readOnly,
    automaticLayout: true,
    folding: true,
    bracketPairColorization: { enabled: true },
    renderValidationDecorations: "on" as const,
  };

  const editorLoading = (
    <Box display="flex" alignItems="center" justifyContent="center" height={height}>
      <CircularProgress size={24} />
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: "relative",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <IconButton
          size="small"
          onClick={() => setExpanded(true)}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 1,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            opacity: 0.7,
            "&:hover": { opacity: 1, bgcolor: "background.paper" },
          }}
        >
          <OpenInFullIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Editor
          height={height}
          defaultLanguage="json"
          value={value}
          onChange={handleChange}
          onMount={handleEditorMount}
          loading={editorLoading}
          options={editorOptions}
          theme="vs"
        />
      </Box>
      <Dialog fullScreen open={expanded} onClose={() => setExpanded(false)}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2">JSON Editor</Typography>
          <IconButton onClick={() => setExpanded(false)} size="small">
            <CloseFullscreenIcon />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Editor
            height="100%"
            defaultLanguage="json"
            value={value}
            onChange={handleChange}
            onMount={handleEditorMount}
            loading={editorLoading}
            options={editorOptions}
            theme="vs"
          />
        </Box>
      </Dialog>
    </>
  );
};
